import { useMemo, useState } from "react";
import {
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup } from "@/components/ui/radio-group";
import { useFormContext } from "react-hook-form";
import { Periods } from "@hometoq/backend/enums";
import { fr } from "date-fns/locale";
import {
  format,
  addDays,
  addMonths,
  startOfDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameDay,
  isSameMonth,
  differenceInDays,
} from "date-fns";
import {
  Sun,
  Moon,
  CalendarRange,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Plus,
  Minus,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "@/lib/utils";

const MIN_DATE = startOfDay(addDays(new Date(), 2));
const MAX_MONTHS_AHEAD = 12;
const WEEKDAYS = ["L", "M", "M", "J", "V", "S", "D"];

const timeSlots = [
  {
    value: Periods.MORNING,
    label: "Déjeuner",
    hours: "12h – 16h",
    icon: <Sun size={20} />,
  },
  {
    value: Periods.AFTERNOON,
    label: "Dîner",
    hours: "19h – 23h",
    icon: <Moon size={20} />,
  },
];

// ─── Custom compact calendar ─────────────────────────────────────────────────

type CalendarProps = {
  value?: Date;
  rangeEnd?: Date;
  mode?: "single" | "range";
  onSelect: (date: Date) => void;
};

function CompactCalendar({
  value,
  rangeEnd,
  mode = "single",
  onSelect,
}: CalendarProps) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const minMonth = useMemo(() => startOfMonth(MIN_DATE), []);
  const maxMonth = useMemo(
    () => addMonths(minMonth, MAX_MONTHS_AHEAD),
    [minMonth]
  );

  const initialMonth = useMemo(() => {
    if (value && value >= MIN_DATE) return startOfMonth(value);
    return minMonth;
  }, [minMonth, value]);

  const [viewMonth, setViewMonth] = useState(initialMonth);
  const [direction, setDirection] = useState<1 | -1>(1);

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    const result: Date[] = [];
    let cur = gridStart;
    while (cur <= gridEnd) {
      result.push(cur);
      cur = addDays(cur, 1);
    }
    return result;
  }, [viewMonth]);

  const canPrev = viewMonth > minMonth;
  const canNext = viewMonth < maxMonth;

  const goPrev = () => {
    if (!canPrev) return;
    setDirection(-1);
    setViewMonth((m) => addMonths(m, -1));
  };
  const goNext = () => {
    if (!canNext) return;
    setDirection(1);
    setViewMonth((m) => addMonths(m, 1));
  };

  return (
    <div className="bg-white border-2 border-border rounded-2xl p-3 select-none">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-3 px-1">
        <button
          type="button"
          onClick={goPrev}
          disabled={!canPrev}
          aria-label="Mois précédent"
          className="w-8 h-8 rounded-full bg-white border border-border flex items-center justify-center text-dark-blue hover:border-primary hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={16} />
        </button>

        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={viewMonth.toISOString()}
            initial={{ opacity: 0, y: direction * -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: direction * 4 }}
            transition={{ duration: 0.18 }}
            className="text-sm font-bold text-dark-blue capitalize"
          >
            {format(viewMonth, "MMMM yyyy", { locale: fr })}
          </motion.span>
        </AnimatePresence>

        <button
          type="button"
          onClick={goNext}
          disabled={!canNext}
          aria-label="Mois suivant"
          className="w-8 h-8 rounded-full bg-white border border-border flex items-center justify-center text-dark-blue hover:border-primary hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1.5">
        {WEEKDAYS.map((d, i) => (
          <span
            key={i}
            className="text-center text-[10px] font-bold text-muted-foreground uppercase tracking-wider"
          >
            {d}
          </span>
        ))}
      </div>

      {/* Day grid */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={viewMonth.toISOString()}
          initial={{ opacity: 0, x: direction * 18 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -18 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="grid grid-cols-7 gap-y-0.5"
        >
          {days.map((day) => {
            const inMonth = isSameMonth(day, viewMonth);
            const disabled = day < MIN_DATE;
            const isStart = !!value && isSameDay(day, value);
            const isEnd = !!rangeEnd && isSameDay(day, rangeEnd);
            const isToday = isSameDay(day, today);
            const inRange =
              mode === "range" &&
              !!value &&
              !!rangeEnd &&
              day > value &&
              day < rangeEnd;
            const highlighted = isStart || isEnd;

            // Range edge rounding
            const roundedClass =
              mode === "range" && (isStart || isEnd || inRange)
                ? cn(
                    isStart && !isEnd && "rounded-r-none",
                    isEnd && !isStart && "rounded-l-none",
                    inRange && "rounded-none"
                  )
                : "";

            return (
              <div key={day.toISOString()} className="relative flex justify-center">
                {/* Range fill (sits behind the button to make a continuous bar) */}
                {inRange && (
                  <span className="absolute inset-y-0.5 inset-x-0 bg-primary/15" />
                )}
                {isStart && !isEnd && mode === "range" && rangeEnd && (
                  <span className="absolute inset-y-0.5 right-0 left-1/2 bg-primary/15" />
                )}
                {isEnd && !isStart && mode === "range" && (
                  <span className="absolute inset-y-0.5 left-0 right-1/2 bg-primary/15" />
                )}

                <motion.button
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(day)}
                  whileTap={!disabled ? { scale: 0.9 } : undefined}
                  transition={{ type: "spring", stiffness: 500, damping: 25 }}
                  className={cn(
                    "relative w-9 h-9 flex items-center justify-center text-sm font-medium rounded-full transition-colors",
                    roundedClass,
                    highlighted
                      ? "bg-primary text-white shadow-sm shadow-primary/30 font-bold z-10"
                      : disabled
                        ? "text-gray-300 cursor-not-allowed"
                        : !inMonth
                          ? "text-muted-foreground/40 hover:bg-primary/5 hover:text-primary"
                          : isToday
                            ? "text-dark-blue font-bold hover:bg-primary/10 hover:text-primary"
                            : "text-dark-blue hover:bg-primary/10 hover:text-primary"
                  )}
                >
                  <span className="relative z-10 tabular-nums">
                    {format(day, "d")}
                  </span>
                  {isToday && !highlighted && (
                    <span className="absolute bottom-1.5 w-1 h-1 rounded-full bg-primary z-10" />
                  )}
                </motion.button>
              </div>
            );
          })}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ─── Step 2 ──────────────────────────────────────────────────────────────────

export const Step2DateTime = () => {
  const form = useFormContext();
  const chefType = form.watch("chefType");
  return chefType === "DEMEURE" ? <DemeureView /> : <DomicileView />;
};

// ─── Vue DOMICILE ────────────────────────────────────────────────────────────

function DomicileView() {
  const form = useFormContext();
  const selectedDate: Date | undefined = form.watch("date");
  const selectedSlot = form.watch("timeSlot");

  return (
    <section className="space-y-5 w-full">
      <div className="flex flex-col gap-1">
        <h1 className="font-h2 text-dark-blue">Date & Créneau</h1>
        <p className="font-p text-muted-foreground">
          Choisissez la date et le moment de votre repas.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-start gap-5">
        {/* Left: Calendar */}
        <FormField
          control={form.control}
          name="date"
          render={({ field }) => (
            <FormItem className="space-y-3 md:w-[300px] md:shrink-0">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Date
              </p>
              <FormControl>
                <CompactCalendar
                  value={field.value}
                  onSelect={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Right: Time slots + summary */}
        <div className="flex-1 space-y-4 min-w-0">
          <FormField
            control={form.control}
            name="timeSlot"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Moment du repas
                </p>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="flex flex-col gap-2.5"
                  >
                    {timeSlots.map((slot) => {
                      const selected = field.value === slot.value;
                      return (
                        <motion.label
                          key={slot.value}
                          whileTap={{ scale: 0.98 }}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-2xl border-2 cursor-pointer transition-colors",
                            selected
                              ? "border-primary bg-primary/5 shadow-sm shadow-primary/10"
                              : "border-border bg-white hover:border-gray-300"
                          )}
                        >
                          <input
                            type="radio"
                            value={slot.value}
                            checked={selected}
                            onChange={() => field.onChange(slot.value)}
                            className="sr-only"
                          />
                          <div
                            className={cn(
                              "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors",
                              selected
                                ? "bg-primary text-white"
                                : "bg-gray-100 text-gray-500"
                            )}
                          >
                            {slot.icon}
                          </div>
                          <div className="min-w-0">
                            <p
                              className={cn(
                                "font-semibold text-sm leading-tight",
                                selected ? "text-primary" : "text-dark-blue"
                              )}
                            >
                              {slot.label}
                            </p>
                            <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                              {slot.hours}
                            </p>
                          </div>
                        </motion.label>
                      );
                    })}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Summary card */}
          <AnimatePresence initial={false}>
            {selectedDate && selectedDate >= MIN_DATE && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-3.5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shrink-0 shadow-sm shadow-primary/30">
                    <CalendarRange size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-dark-blue capitalize truncate">
                      {format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {selectedSlot === Periods.MORNING
                        ? "Déjeuner — 12h-16h"
                        : "Dîner — 19h-23h"}
                    </p>
                  </div>
                  <Sparkles size={14} className="text-primary shrink-0" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

// ─── Vue DEMEURE ─────────────────────────────────────────────────────────────

function DemeureView() {
  const form = useFormContext();
  const dateFrom: Date | undefined = form.watch("date");
  const dateTo: Date | undefined = form.watch("dateTo");
  const hasValidStart = !!dateFrom && dateFrom >= MIN_DATE;
  const nbDays =
    hasValidStart && dateTo ? differenceInDays(dateTo, dateFrom) + 1 : null;

  const handlePick = (date: Date) => {
    // No valid start, or click before/on current start → set new start (1 day)
    if (!hasValidStart || date <= (dateFrom as Date)) {
      form.setValue("date", date, { shouldValidate: true });
      form.setValue("dateTo" as never, date as never, {
        shouldValidate: true,
      });
      return;
    }
    // Click after start → set as end
    form.setValue("dateTo" as never, date as never, { shouldValidate: true });
  };

  const adjustDays = (delta: number) => {
    if (!hasValidStart) return;
    const current = nbDays ?? 1;
    const next = Math.max(1, current + delta);
    form.setValue(
      "dateTo" as never,
      addDays(dateFrom as Date, next - 1) as never,
      { shouldValidate: true }
    );
  };

  return (
    <section className="space-y-5 w-full">
      <div className="flex flex-col gap-1">
        <h1 className="font-h2 text-dark-blue">Période du séjour</h1>
        <p className="font-p text-muted-foreground">
          Cliquez la date d&apos;arrivée puis celle de départ — ou ajustez la
          durée.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-start gap-5">
        {/* Left: Calendar */}
        <FormField
          control={form.control}
          name="date"
          render={({ field }) => (
            <FormItem className="space-y-3 md:w-[300px] md:shrink-0">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Sélection des dates
              </p>
              <FormControl>
                <CompactCalendar
                  mode="range"
                  value={field.value}
                  rangeEnd={dateTo}
                  onSelect={handlePick}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Right: Duration + summary */}
        <div className="flex-1 space-y-4 min-w-0">
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Durée
            </p>
            <div className="bg-white border-2 border-border rounded-2xl p-4 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => adjustDays(-1)}
                disabled={!hasValidStart || (nbDays ?? 1) <= 1}
                className="w-11 h-11 rounded-xl border-2 border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Minus size={18} />
              </button>

              <div className="flex items-end gap-2">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.span
                    key={nbDays ?? 0}
                    initial={{ y: 10, opacity: 0, scale: 0.9 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: -10, opacity: 0, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 360, damping: 26 }}
                    className="text-4xl font-bold text-dark-blue leading-none tabular-nums"
                  >
                    {nbDays ?? 1}
                  </motion.span>
                </AnimatePresence>
                <span className="text-muted-foreground font-medium pb-0.5 text-sm">
                  jour{(nbDays ?? 1) > 1 ? "s" : ""}
                </span>
              </div>

              <button
                type="button"
                onClick={() => adjustDays(1)}
                disabled={!hasValidStart}
                className="w-11 h-11 rounded-xl bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* Summary card */}
          <AnimatePresence initial={false}>
            {dateFrom && dateTo && hasValidStart && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-3.5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shrink-0 shadow-sm shadow-primary/30">
                    <CalendarRange size={18} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                    <span className="font-bold text-dark-blue text-sm capitalize">
                      {format(dateFrom, "d MMM", { locale: fr })}
                    </span>
                    <ArrowRight size={14} className="text-primary shrink-0" />
                    <span className="font-bold text-dark-blue text-sm capitalize">
                      {format(dateTo, "d MMM yyyy", { locale: fr })}
                    </span>
                    <span className="ml-1 px-2 py-0.5 rounded-full bg-primary text-white text-[11px] font-bold">
                      {nbDays} jour{nbDays! > 1 ? "s" : ""}
                    </span>
                  </div>
                  <Sparkles size={14} className="text-primary shrink-0" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {(form.formState.errors as Record<string, { message?: string }>)[
            "dateTo"
          ] && (
            <p className="text-sm font-medium text-destructive">
              {
                (form.formState.errors as Record<string, { message?: string }>)[
                  "dateTo"
                ]?.message
              }
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
